import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Loader from '../components/Loader';
import useLoading from '../hooks/useLoading';
import useImagePreload from '../hooks/useImagePreload';
import {
    HomeIcon,
    BuildingOfficeIcon,
    BriefcaseIcon,
    MapPinIcon,
    ArrowRightIcon,
    ChevronRightIcon
} from '@heroicons/react/24/outline';

const Projects = () => {
    const [activeFilter, setActiveFilter] = useState('all');
    const [hoveredProject, setHoveredProject] = useState(null);
    const isLoading = useLoading(1500);

    const projectImages = [
        'https://images.unsplash.com/photo-1600210492486-724fe5c67fb0',
        'https://images.unsplash.com/photo-1537726235470-8504e3beef77',
        'https://images.unsplash.com/photo-1497366216548-37526070297c',
        'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c',
        'https://images.unsplash.com/photo-1590381105924-c72589b9ef3f',
        'https://images.unsplash.com/photo-1497366811353-6870744d04b2',
    ];

    const imagesLoaded = useImagePreload(projectImages);

    const filters = [
        { id: 'all', label: 'All Projects', icon: HomeIcon },
        { id: 'residential', label: 'Residential', icon: HomeIcon },
        { id: 'commercial', label: 'Commercial', icon: BuildingOfficeIcon },
        { id: 'corporate', label: 'Corporate', icon: BriefcaseIcon },
    ];

    const projects = [
        {
            id: 1,
            title: 'Modern Minimalist Villa',
            category: 'residential',
            description: 'A contemporary approach to luxury living with clean lines and natural materials.',
            image: 'https://images.unsplash.com/photo-1600210492486-724fe5c67fb0',
            location: 'Beverly Hills, CA',
            features: ['Open Floor Plan', 'Natural Materials', 'Smart Home Integration'],
            year: '2023'
        },
        {
            id: 2,
            title: 'Luxury Restaurant Design',
            category: 'commercial',
            description: 'An elegant dining space that combines comfort with sophistication.',
            image: 'https://images.unsplash.com/photo-1537726235470-8504e3beef77',
            location: 'Manhattan, NY',
            features: ['Custom Lighting', 'Acoustic Design', 'Outdoor Seating'],
            year: '2023'
        },
        {
            id: 3,
            title: 'Contemporary Office Space',
            category: 'corporate',
            description: 'A modern workplace designed for productivity and collaboration.',
            image: 'https://images.unsplash.com/photo-1497366216548-37526070297c',
            location: 'Chicago, IL',
            features: ['Collaborative Areas', 'Ergonomic Design', 'Green Spaces'],
            year: '2023'
        },
        {
            id: 4,
            title: 'Urban Loft Renovation',
            category: 'residential',
            description: 'Industrial elements meet modern comfort in this urban transformation.',
            image: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c',
            location: 'Seattle, WA',
            features: ['Exposed Brick', 'High Ceilings', 'Custom Storage'],
            year: '2023'
        },
        {
            id: 5,
            title: 'Boutique Hotel Lobby',
            category: 'commercial',
            description: 'A welcoming space that sets the tone for an exceptional stay.',
            image: 'https://images.unsplash.com/photo-1590381105924-c72589b9ef3f',
            location: 'Miami, FL',
            features: ['Art Installation', 'Lounge Areas', 'Concierge Desk'],
            year: '2023'
        },
        {
            id: 6,
            title: 'Tech Startup Headquarters',
            category: 'corporate',
            description: 'A dynamic environment that fosters innovation and creativity.',
            image: 'https://images.unsplash.com/photo-1497366811353-6870744d04b2',
            location: 'San Francisco, CA',
            features: ['Innovation Hub', 'Breakout Spaces', 'Wellness Center'],
            year: '2023'
        },
    ];

    const filteredProjects = activeFilter === 'all'
        ? projects
        : projects.filter(project => project.category === activeFilter);

    if (isLoading || !imagesLoaded) {
        return <Loader />;
    }

    return (
        <div className="min-h-screen bg-dark">
            {/* Hero Section */}
            <section className="relative py-24 bg-primary text-light">
                <div className="absolute inset-0 bg-gradient-to-b from-primary/90 via-primary/70 to-primary/90" />
                <div className="container mx-auto px-4 relative">
                    <motion.div
                        className="text-center"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                    >
                        <h1 className="font-serif text-5xl md:text-6xl font-bold mb-6">Our Projects</h1>
                        <p className="text-xl text-light/90 max-w-2xl mx-auto">
                            Explore our portfolio of thoughtfully designed spaces that inspire and transform
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* Projects Section */}
            <section className="py-24">
                <div className="container mx-auto px-4">
                    {/* Filters */}
                    <div className="flex flex-wrap justify-center gap-4 mb-16">
                        {filters.map((filter) => (
                            <motion.button
                                key={filter.id}
                                onClick={() => setActiveFilter(filter.id)}
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                className={`flex items-center space-x-2 px-6 py-3 rounded-full transition-all duration-300 ${activeFilter === filter.id
                                    ? 'bg-primary text-light'
                                    : 'bg-dark/50 text-light/80 hover:bg-dark/70'
                                    }`}
                            >
                                <filter.icon className="w-5 h-5" />
                                <span>{filter.label}</span>
                            </motion.button>
                        ))}
                    </div>

                    {/* Projects Grid */}
                    <motion.div
                        layout
                        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
                    >
                        <AnimatePresence>
                            {filteredProjects.map((project) => (
                                <motion.div
                                    key={project.id}
                                    layout
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -20 }}
                                    transition={{ duration: 0.5 }}
                                    onHoverStart={() => setHoveredProject(project.id)}
                                    onHoverEnd={() => setHoveredProject(null)}
                                    className="card group overflow-hidden hover:bg-dark/70 transition-all duration-300"
                                    whileHover={{ y: -5 }}
                                >
                                    <div className="relative h-80 overflow-hidden">
                                        <motion.img
                                            src={project.image}
                                            alt={project.title}
                                            className="w-full h-full object-cover"
                                            whileHover={{ scale: 1.1 }}
                                            transition={{ duration: 0.7 }}
                                            loading="lazy"
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                                        <div className="absolute top-4 right-4 bg-primary/90 text-light px-4 py-1 rounded-full text-sm backdrop-blur-sm">
                                            {filters.find(f => f.id === project.category)?.label}
                                        </div>
                                        <motion.div
                                            className="absolute bottom-0 left-0 right-0 p-6"
                                            initial={{ y: 100 }}
                                            whileHover={{ y: 0 }}
                                            transition={{ duration: 0.3 }}
                                        >
                                            <div className="space-y-2">
                                                {project.features.map((feature, index) => (
                                                    <div key={index} className="flex items-center text-light">
                                                        <ChevronRightIcon className="w-4 h-4 mr-2" />
                                                        {feature}
                                                    </div>
                                                ))}
                                            </div>
                                        </motion.div>
                                    </div>
                                    <div className="p-6">
                                        <div className="flex justify-between items-start mb-4">
                                            <h3 className="font-serif text-2xl font-bold text-light">
                                                {project.title}
                                            </h3>
                                            <span className="text-accent">{project.year}</span>
                                        </div>
                                        <p className="text-light/80 mb-4">
                                            {project.description}
                                        </p>
                                        <div className="flex items-center text-accent">
                                            <MapPinIcon className="w-5 h-5 mr-2" />
                                            {project.location}
                                        </div>
                                    </div>
                                </motion.div>
                            ))}
                        </AnimatePresence>
                    </motion.div>

                    {/* Empty State */}
                    {filteredProjects.length === 0 && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            className="text-center py-16"
                        >
                            <p className="text-xl text-light/80">No projects found in this category.</p>
                        </motion.div>
                    )}
                </div>
            </section>
        </div>
    );
};

export default Projects; 